import React, { useState } from "react";
import PropTypes from "prop-types";
import Workspace from "../common/workspace";
import LoadingWrapper from "../common/loading-wrapper";
import { getProjects, createProject, deleteProjectById, updateProjectById } from "../../services/api-users";
import Helmet from "react-helmet";
import { withRouter } from "react-router";
import { withUser } from "@unifiedpatents/sso";
import useSWR, { mutate } from "swr";
import { Button, Modal, Input, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";

const EditAction = ({ project, userId }) => {
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [name, setName] = useState(project.name);
  const [summary, setSummary] = useState(project.summary);
  const [updateLoading, setUpdateLoading] = useState(false);
  return (
    <>
      <Tooltip title="Edit Project">
        <Button icon={<EditOutlined />} onClick={() => setShowEditPanel(true)} />
      </Tooltip>
      <Modal
        title="Edit Project"
        confirmLoading={updateLoading}
        open={showEditPanel}
        onOk={() => {
          setUpdateLoading(true);
          updateProjectById(userId, project.id, { name, summary })
            .then(() => {
              setUpdateLoading(false);
              mutate(`${userId}/projects`);
              setShowEditPanel(false);
            })
            .catch(() => {
              setUpdateLoading(false);
              setShowEditPanel(false);
            });
        }}
        onCancel={() => setShowEditPanel(false)}>
        <Input type="text" placeholder="Name" value={name} onChange={e => setName(e.target.value)} />
        <Input.TextArea placeholder="Summary" value={summary} onChange={e => setSummary(e.target.value)} style={{ marginTop: 16 }} />
      </Modal>
    </>
  )
}

EditAction.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    summary: PropTypes.string
  }).isRequired,
  userId: PropTypes.string.isRequired
};

const Projects = ({ router, user, children }) => {
  const { project: projectId } = router.params;
  const {
    status: { isLoggedIn },
    metadata: { authorization },
    profile: { user_id }
  } = user;
  const accessLevel = authorization.roles.indexOf("member-data") >= 0 ? "full" : isLoggedIn ? "guest" : "public";
  const helmet = {
    title: "Portfolio Projects",
    meta: [
      {
        name: "keywords",
        content: "Search, Patent, Patent Portfolio, Patent Landscape, Portfolio Value"
      },
      {
        name: "description",
        content: "Patent Portfolio Projects"
      }
    ]
  };
  const { data, isLoading: projectsLoading } = useSWR(
    `${user_id}/projects`,
    () => getProjects(user_id)
  );
  const projects = data && data.length > 0 ? data : [];
  const currentProject = projects.find(project => project.id === projectId);
  const refreshProjects = () => {
    mutate(`${user_id}/projects`);
  };
  const addNewProject = ({ name, summary }) => {
    return createProject(user_id, { name: name, summary: summary })
      .then(projects => {
        router.push(`/patents/portfolio/my/${projects[0].id}`)
        refreshProjects()
      })
  }
  const removeProject = id => {
    return deleteProjectById(user_id, id)
      .then(() => {
        if (id === projectId) router.push("/patents/portfolio");
        refreshProjects();
      });
  }
  const workspaceProps = accessLevel !== "full"
    ? { disabled: true }
    : {
      disabled: false,
      currentProject,
      onSelectProject: project => router.push(`/patents/portfolio/my/${project.id}`),
      onCreateProject: () => addNewProject({ name: `Project ${new Date().toLocaleDateString()}`, summary: "" }),
      onDeleteProject: ({ id }) => removeProject(id),
      extraActions: currentProject ? [<EditAction key="edit" userId={user_id} />] : undefined
    };
  return (
    <>
      <Helmet {...helmet} />
      <LoadingWrapper loading={projectsLoading}>
        <Workspace
          title="Portfolio Projects"
          projects={projects}
          {...workspaceProps}
        >
          {children}
        </Workspace>
      </LoadingWrapper>
    </>
  );
}

export default withUser(withRouter(Projects));
